import { Portal } from "@chakra-ui/portal";
import React, { useState } from "react";
import { CircleMenu, CircleMenuItem } from "react-circular-menu";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import {
  MdDeleteOutline,
  MdEdit,
  MdInfoOutline,
  MdMenu,
  MdPushPin,
  MdSettings,
  MdVisibility,
} from "react-icons/md";
import { TbCircleLetterS } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import CircleButton from "widgets/controls/buttons/circle-button";
import TooltipHorizon from "widgets/controls/tooltip";
import { CONST } from "models/constants";
import { usePermissions } from "hooks/usePermissions";

type KanbanCircleMenuProps = {
  data: any;
  onClick?: (open: boolean) => void;
  onClose?: (refresh: boolean) => void;
};

type MenuItem = {
  icon?: IconType;
  image?: JSX.Element;
  tooltip: string;
  onClick: () => void;
  permissionRole?: string;
};

function KanbanCircleMenu(props: KanbanCircleMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { onClick, data } = props;
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ x: 0, y: 0 });
  const [enableSelect, setEnableSelect] = useState(false);
  const { canShow } = usePermissions();
  const items: MenuItem[] = [
    {
      icon: MdVisibility,
      tooltip: t("generic.action.view"),
      onClick: () => {
        setMenuOpen(false);
        onClick?.(false);
        navigate(`/admin/devices/device?snr=${data?.device?.snr}`);
      },
      permissionRole: CONST.ROLE_USER,
    },
    {
      icon: MdSettings,
      tooltip: t("generic.action.dev.tools"),
      onClick: () => {
        setMenuOpen(false);
        onClick?.(false);
        navigate(`/admin/devices/devtools?snr=${data?.device?.snr}`);
      },
      permissionRole: CONST.ROLE_SUPER,
    },
  ];

  const onMenuStateChange = (open: boolean) => {
    setMenuOpen(open);
    onClick?.(open);
    setEnableSelect(false);

    setTimeout(() => {
      setEnableSelect(open);
    }, 500);
  };

  return (
    <>
      <div>
        <div
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            setMenuPos({ x: e.clientX, y: e.clientY });
            onMenuStateChange(!menuOpen);
          }}
        >
          <MdInfoOutline className="text-3xl text-blue-500" />
        </div>
      </div>

      <Portal>
        <div className={`absolute`} style={{ top: menuPos.y, left: menuPos.x }}>
          <CircleMenu
            startAngle={-75}
            rotationAngle={120}
            itemSize={2}
            radius={4}
            rotationAngleInclusive={false}
            open={menuOpen}
            onMenuToggle={() => {
              onMenuStateChange(!menuOpen);
            }}
            menuToggleElement={<></>}
          >
            {items.map(
              (item, index) =>
                canShow(item.permissionRole) && (
                  <CircleMenuItem
                    key={index}
                    style={{
                      backgroundColor: "#1b3bbb",
                      opacity: 0.95,
                      borderWidth: 0,
                    }}
                    onClick={() => {
                      if (
                        item.permissionRole &&
                        !canShow(item.permissionRole)
                      ) {
                        return;
                      }
                      item.onClick();
                    }}
                  >
                    <TooltipHorizon
                      extra="custom-class"
                      trigger={
                        <item.icon className="rounded-full p-0 text-3xl text-white" />
                      }
                      content={<span>{item.tooltip}</span>}
                      placement={
                        index === 0
                          ? "top"
                          : index === items.length - 1
                          ? "bottom"
                          : "right"
                      }
                    />
                  </CircleMenuItem>
                )
            )}
          </CircleMenu>
        </div>
      </Portal>
    </>
  );
}

export default KanbanCircleMenu;
